import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ArrowUpCircle, ArrowDownCircle, Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StockLedger = () => {
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [userRole, setUserRole] = useState('user');

  useEffect(() => {
    fetchLedger();
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role || 'user');
      }
    } catch (e) {
      console.warn('Failed to decode auth token for role', e);
    }
  }, []);

  const fetchLedger = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stock-ledger`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLedger(response.data);
    } catch (error) {
      toast.error('Failed to fetch stock ledger');
    } finally {
      setLoading(false);
    }
  };

  const filteredLedger = ledger.filter((entry) => {
    const matchesSearch = entry.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.reference_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || entry.transaction_type === filterType;
    return matchesSearch && matchesType;
  });

  const canExport = ['admin', 'manager', 'staff', 'accountant'].includes(userRole);

  const handleExportInventory = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      if (searchTerm) payload.search = searchTerm;
      
      const response = await axios.post(`${API}/export/inventory`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Inventory_Report_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Inventory report exported');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full" data-testid="stock-ledger-loading">Loading stock ledger...</div>;
  }

  return (
    <div className="space-y-6" data-testid="stock-ledger-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-primary" data-testid="stock-ledger-title">Stock Ledger</h1>
          <p className="text-slate-600 mt-1" data-testid="stock-ledger-subtitle">Complete history of all stock movements</p>
        </div>
        {canExport && (
          <>
          <Button variant="outline" onClick={handleExportInventory} disabled={exporting} className="gap-2">
            {exporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            Export Inventory Report
          </Button>
          <CsvDownloadButton onDownload={handleExportInventory} testId="stock-ledger-export-csv" size="default" disabled={exporting} />
          </>
        )}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                data-testid="ledger-search-input"
                placeholder="Search by product or reference number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48" data-testid="ledger-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="filter-all">All Transactions</SelectItem>
                <SelectItem value="inward" data-testid="filter-inward">Inward Only</SelectItem>
                <SelectItem value="outward" data-testid="filter-outward">Outward Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ledger-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Date & Time
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Product
                  </th>
                  <th className="text-center py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Type
                  </th>
                  <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Quantity
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Reference
                  </th>
                  <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                    Ref Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredLedger.map((entry) => (
                  <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${entry.id}`}>
                    <td className="py-3 px-4 text-xs text-slate-600" data-testid={`ledger-time-${entry.id}`}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 font-medium" data-testid={`ledger-product-${entry.id}`}>{entry.product_name}</td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {entry.transaction_type === 'inward' ? (
                          <ArrowDownCircle size={18} className="text-green-600" data-testid={`ledger-icon-inward-${entry.id}`} />
                        ) : (
                          <ArrowUpCircle size={18} className="text-red-600" data-testid={`ledger-icon-outward-${entry.id}`} />
                        )}
                        <span
                          className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            entry.transaction_type === 'inward'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                          data-testid={`ledger-type-${entry.id}`}
                        >
                          {entry.transaction_type}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-semibold" data-testid={`ledger-quantity-${entry.id}`}>
                      {entry.transaction_type === 'inward' ? '+' : '-'}
                      {entry.quantity}
                    </td>
                    <td className="py-3 px-4 font-mono text-xs" data-testid={`ledger-reference-${entry.id}`}>{entry.reference_number}</td>
                    <td className="py-3 px-4 text-xs" data-testid={`ledger-ref-type-${entry.id}`}>
                      <span className="inline-flex px-2 py-1 rounded bg-slate-100 text-slate-700">
                        {entry.reference_type}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredLedger.length === 0 && (
            <div className="text-center py-12 text-slate-500" data-testid="no-ledger-message">
              No stock movements found.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StockLedger;
